import type { SeverityId } from '../../types/ids';
import type { DetectedTension, DiagnosticInsight } from '../../types/result';
import { CRITICAL_QUESTION_RECOMMENDATIONS } from './criticalQuestionRecommendations';

/** Je niedriger die Rangzahl, desto höher die fachliche Priorität (Abschnitt 21.5). */
export const RecommendationRank = {
  MissingFoundation: 0,
  CriticalTension: 1,
  ImportantTension: 2,
  NoticeTension: 3,
} as const;
export type RecommendationRank = (typeof RecommendationRank)[keyof typeof RecommendationRank];

const severityRank: Record<SeverityId, RecommendationRank> = {
  critical: RecommendationRank.CriticalTension,
  important: RecommendationRank.ImportantTension,
  notice: RecommendationRank.NoticeTension,
};

export interface RankedRecommendationId {
  id: string;
  rank: RecommendationRank;
}

/**
 * Sammelt Empfehlungs-IDs aus erkannten Spannungsfeldern und kritischen Engpässen (Abschnitt 21.2).
 * Dedupliziert dabei bereits auf ID-Ebene (Abschnitt 21.3) und behält die höchste Priorität pro ID.
 */
export function collectRecommendations(
  detectedTensions: readonly DetectedTension[],
  weakPoints: readonly DiagnosticInsight[],
  businessCriticalQuestionIds: readonly string[],
): RankedRecommendationId[] {
  const bestRank = new Map<string, RecommendationRank>();

  function addRecommendation(id: string, rank: RecommendationRank): void {
    const existing = bestRank.get(id);
    if (existing === undefined || rank < existing) {
      bestRank.set(id, rank);
    }
  }

  for (const tension of detectedTensions) {
    const rank = severityRank[tension.severity];
    for (const recommendationId of tension.recommendationIds) {
      addRecommendation(recommendationId, rank);
    }
  }

  const criticalSet = new Set(businessCriticalQuestionIds);
  for (const weakPoint of weakPoints) {
    const isCritical = weakPoint.sourceQuestionIds.some((id) => criticalSet.has(id));
    if (!isCritical) continue;
    for (const questionId of weakPoint.sourceQuestionIds) {
      const mapped = CRITICAL_QUESTION_RECOMMENDATIONS[questionId];
      if (!mapped) continue;
      for (const recommendationId of mapped) {
        addRecommendation(recommendationId, RecommendationRank.MissingFoundation);
      }
    }
  }

  return [...bestRank.entries()]
    .map(([id, rank]) => ({ id, rank }))
    .sort((a, b) => a.rank - b.rank);
}
